// Admin → Supplier Discounts oversight page.
//
// Read-only cross-tenant view of every (supplier, retailer, category, range, %)
// allocation. Visible in the main admin sidebar.
//
// Backend: GET /api/admin/supplier-discounts (returns all rows from
// supplier_retailer_discounts joined with supplier + retailer + category names).
//
// Layout: search box + grouped rows (by supplier → retailer → category).
// Sortable client-side; deletes are not exposed here — admin should ask the
// supplier to manage their own allocations from their portal.

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

interface DiscountRow {
  id: string;
  supplierName: string;
  supplierSlug: string;
  retailerName: string;
  retailerSlug: string;
  categoryName: string | null;
  doorRange: string | null;
  discountPercent: number;
  updatedAt: string | null;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return "—"; }
}

const supplierDiscountsPage: Page = {
  name: "supplier-discounts",
  label: "Discounts",
  icon: "💷",
  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();

    let rows: DiscountRow[] = [];
    let loading = true;
    let loadError: string | null = null;
    let searchQuery = "";

    container.innerHTML = `
      <header class="asd-header">
        <div class="asd-header__titles">
          <h1>Supplier discounts</h1>
          <p class="asd-subtitle">Every per-retailer / per-category / per-doorRange allocation set by every supplier on the platform. Read-only oversight — suppliers manage their own from their portal.</p>
        </div>
        <input type="search" class="adm-input asd-search" data-role="search"
               placeholder="Search supplier, retailer, category, range…" />
      </header>

      <div class="asd-stats" data-role="stats"></div>

      <div data-role="body"></div>
    `;

    const bodyEl   = container.querySelector<HTMLElement>("[data-role='body']")!;
    const statsEl  = container.querySelector<HTMLElement>("[data-role='stats']")!;
    const searchEl = container.querySelector<HTMLInputElement>("[data-role='search']")!;

    function renderStats(filteredCount: number): void {
      const suppliers = new Set(rows.map((r) => r.supplierSlug)).size;
      const retailers = new Set(rows.map((r) => r.retailerSlug)).size;
      statsEl.innerHTML = `
        <div class="adm-card asd-stat">
          <div class="asd-stat__lbl">Total allocations</div>
          <div class="asd-stat__val">${rows.length.toLocaleString()}</div>
        </div>
        <div class="adm-card asd-stat">
          <div class="asd-stat__lbl">Suppliers with discounts set</div>
          <div class="asd-stat__val">${suppliers}</div>
        </div>
        <div class="adm-card asd-stat">
          <div class="asd-stat__lbl">Retailers covered</div>
          <div class="asd-stat__val">${retailers}</div>
        </div>
        <div class="adm-card asd-stat">
          <div class="asd-stat__lbl">Showing</div>
          <div class="asd-stat__val">${filteredCount.toLocaleString()}</div>
        </div>
      `;
    }

    function applyFilter(): DiscountRow[] {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return rows;
      return rows.filter((r) => {
        const hay = [
          r.supplierName, r.supplierSlug,
          r.retailerName, r.retailerSlug,
          r.categoryName ?? "",
          r.doorRange ?? "",
        ].join(" ").toLowerCase();
        return hay.includes(q);
      });
    }

    function renderBody(): void {
      if (loading) {
        bodyEl.innerHTML = `<div class="adm-loading">Loading…</div>`;
        return;
      }
      if (loadError) {
        bodyEl.innerHTML = `<div class="adm-error">${escapeHtml(loadError)}</div>`;
        return;
      }
      const list = applyFilter();
      renderStats(list.length);
      if (list.length === 0) {
        bodyEl.innerHTML = rows.length === 0
          ? `<div class="adm-empty">No discount allocations set yet. Suppliers configure these from their portal.</div>`
          : `<div class="adm-empty">No matches for "${escapeHtml(searchQuery)}".</div>`;
        return;
      }
      bodyEl.innerHTML = `
        <div class="adm-card asd-card">
          <table class="adm-table asd-table">
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Retailer</th>
                <th>Category</th>
                <th>Door range</th>
                <th class="asd-num">Discount %</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              ${list.map((r) => {
                const rangeCell = r.doorRange
                  ? escapeHtml(r.doorRange)
                  : `<span class="asd-fallback-pill">All ranges</span>`;
                return `
                  <tr>
                    <td><b>${escapeHtml(r.supplierName)}</b></td>
                    <td>${escapeHtml(r.retailerName)}</td>
                    <td>${escapeHtml(r.categoryName ?? "(uncategorised)")}</td>
                    <td>${rangeCell}</td>
                    <td class="asd-num">${r.discountPercent.toFixed(2)}%</td>
                    <td>${escapeHtml(formatDate(r.updatedAt))}</td>
                  </tr>`;
              }).join("")}
            </tbody>
          </table>
        </div>
      `;
    }

    async function load(): Promise<void> {
      try {
        const resp = await ctx.api<{ ok: boolean; discounts?: DiscountRow[] }>(
          "/admin/supplier-discounts",
          { signal: ac.signal },
        );
        if (ac.signal.aborted) return;
        rows = resp.discounts ?? [];
        loading = false;
        loadError = null;
        renderBody();
      } catch (err) {
        if (ac.signal.aborted) return;
        loading = false;
        loadError = err instanceof ApiError
          ? `Couldn't load discounts (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "Couldn't load discounts";
        renderBody();
      }
    }

    // Search with a small debounce so each keystroke doesn't re-render the
    // whole table — important when the list grows large.
    let searchTimer: number | undefined;
    const onSearch = (e: Event): void => {
      searchQuery = (e.target as HTMLInputElement).value;
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => renderBody(), 120);
    };
    searchEl.addEventListener("input", onSearch);

    void load();

    return () => {
      ac.abort();
      window.clearTimeout(searchTimer);
      searchEl.removeEventListener("input", onSearch);
    };
  },
};

registerPage(supplierDiscountsPage);
